import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isNil, compact, cloneDeep, sortBy } from 'lodash';
import { Button } from 'reactstrap';
import _ControlImport from 'react-leaflet-control';
import { roundToNearestMinutes, addSeconds } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import PaymentMethodDropdown from './booking/PaymentMethodDropdown';
import UserSelect from './booking/UserSelect';
import PreBookingShortcutsModal from './booking/PreBookingShortcutsModal';
import PrebookingTime from './booking/PrebookingTime';
import PassengerNoteToDriver from './booking/PassengerNoteToDriver';
import RideDetails from './booking/RideDetails';
import StellwerkAPI from '../lib/stellwerk_api';
import Alert from './bootstrap/Alert';
import Card from './bootstrap/Card';
import { secondsToHuman, formattedTime, timeDiff } from '../lib/view_helpers';
import { rideDuration, cleanExistingWaypoint } from '../lib/booking_helpers';
import BookingWizardController from '../lib/booking_wizard/controller';
import {
  applyOptionValue,
  initialPaymentMethods,
  initialRideOptionValues,
  newUninitializedPassenger,
  productRequiresFixedStation,
  productSupportsPassengerNoteToDriver,
  productSupportsPrebookingAssistance,
  rideIsBookable,
  rideIsCreateable,
  rideNeedsPaymentMethod
} from '../lib/booking_wizard/predicates';
import WaypointSwitcher from './booking/WaypointSwitcher';
import WaypointSelector from './booking/WaypointSelector';
import RideOptions from './booking/RideOptions';
import HereMap from './here_maps/HereMap';
import Station from './here_maps/markers/Station';
import SVGMarker from './here_maps/markers/SVGMarker';
import { latLngFromObj } from '../lib/map_helpers';
import pickupIcon from '../images/maps/svg_icons/pickup.svg';
import dropoffIcon from '../images/maps/svg_icons/dropoff.svg';
import Area from './here_maps/layers/Area';
import Ride from './here_maps/Ride';

const Control = _ControlImport?.default ?? _ControlImport;

const colors = {
  black: '#000',
  green: '#00e069',
  red: '#ff2b79'
};

export default class BookingWizard extends React.Component {
  constructor(props) {
    super(props);

    this.api = new StellwerkAPI(props.product.id);
    this.secondRefreshTimer = null;

    this.state = this.initialState(props);

    this.setDestination = this.setDestination.bind(this);
    this.setOrigin = this.setOrigin.bind(this);
    this.setRequestedTime = this.setRequestedTime.bind(this);
    this.setUser = this.setUser.bind(this);
    this.setChosenPaymentMethod = this.setChosenPaymentMethod.bind(this);
    this.initiateStateFromClonedRide = this.initiateStateFromClonedRide.bind(this);
    this.switchFixedWaypoints = this.switchFixedWaypoints.bind(this);
    this.setOption = this.setOption.bind(this);
    this.setPassengers = this.setPassengers.bind(this);
    this.addPassenger = this.addPassenger.bind(this);
    this.setNearbyMatchingMode = this.setNearbyMatchingMode.bind(this);

    this.setPassengerNoteToDriver = this.setPassengerNoteToDriver.bind(this);


    this.controller = new BookingWizardController({
      api: this.api,
      props: { areas: props.areas, cloningRide: props.cloningRide, product: props.product },
      getState: () => this.state,
      setState: patch => this.setState(patch),
      setOption: this.setOption,
      startTicker: () => this.startTicker(),
      endTicker: () => this.endTicker()
    });

    this.createRideInquiry = this.controller.createRideInquiry;
    this.bookRide = this.controller.bookRide;
    this.createRide = this.controller.createRide;
    this.cancelRide = this.controller.cancelRide;
    this.fetchUserBootstrap = this.controller.fetchUserBootstrap;

    this.resetWizard = this.resetWizard.bind(this);
    this.bookAgain = this.bookAgain.bind(this);
    this.bookReturn = this.bookReturn.bind(this);

    this.togglePrebookingModal = this.togglePrebookingModal.bind(this);
    this.toggleStationVisibility = this.toggleStationVisibility.bind(this);
  }

  // LIFECYCLE METHODS ---------------------------------------------------------

  componentDidMount() {
    if (isEmpty(this.props.cloningRide)) {
      if (this.props.product.requires_fixed_station) {
        // Setting up a default value
        this.switchFixedWaypoints('origin');
      }
    } else {
      this.controller.showSpinner(window.locales.BookingWizard.spinners.cloningRide);
      this.initiateStateFromClonedRide();
      this.controller.hideSpinner();
    }

    this.createRideInquiry();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isNil(this.state.userId) && this.state.userId !== prevState.userId) {
      this.fetchUserBootstrap(this.state.userId);
    }

    if (this.state.requestedTime !== prevState.requestedTime ||
      this.state.origin !== prevState.origin ||
      this.state.destination !== prevState.destination ||
      this.state.nearbyMatchingMode !== prevState.nearbyMatchingMode ||
      this.state.passengers !== prevState.passengers ||
      this.state.options !== prevState.options
    ) {
      this.createRideInquiry();
    }
  }

  componentWillUnmount() {
    this.endTicker();
  }

  // STATE MANAGEMENT ----------------------------------------------------------

  setUser(userId) {
    this.setState({ userId });
  }

  setOrigin(origin) {
    this.setState({ origin });
  }

  setDestination(destination) {
    this.setState({ destination });
  }

  setRequestedTime(requestedTime, requestedTimeForWaypoint) {
    if (!this.props.product.ad_hoc_bookable && !requestedTime) {
      return;
    }

    this.setState((prevState) => ({
      requestedTime,
      requestedTimeForWaypoint: requestedTimeForWaypoint || prevState.requestedTimeForWaypoint
    }));
  }

  // The updater form is required: a cloned ride sets every one of its options in a loop, and React batches those
  // updates, so reading this.state would hand each call the options from before the loop started
  setOption(slug, value) {
    this.setState(prevState => {
      const options = applyOptionValue(prevState.options, slug, value);

      return options === null ? null : { options };
    });
  }

  setNearbyMatchingMode(waypointType, value) {
    const { nearbyMatchingMode } = this.state;

    nearbyMatchingMode[waypointType] = value;

    // `cloneDeep` and further down `compact` on the collections was necessary to make `componentDidUpdate` actually
    // pick up the changes reliably. So I'd say in more general terms changes weren't picked up on the same object but
    // with creation of a new collection object the changes were picked up.
    this.setState({ nearbyMatchingMode: cloneDeep(nearbyMatchingMode) });
  }

  setPassengerNoteToDriver(note) {
    this.setState({ passengerNoteToDriver: note });
  }

  setPassengers(index, passengerData) {
    this.setState((state) => {
      const { passengers } = state;
      passengers[index] = passengerData;
      return { passengers: compact(passengers) };
    });
  }

  setChosenPaymentMethod(chosenPaymentMethod) {
    this.setState({ chosenPaymentMethod });
  }

  addPassenger() {
    this.setState((state) => {
      const { passengers } = state;
      passengers.push(newUninitializedPassenger(this.props.product));

      // `cloneDeep` and further down `compact` on the collections was necessary to make `componentDidUpdate` actually
      // pick up the changes reliably. So I'd say in more general terms changes weren't picked up on the same object but
      // with creation of a new collection object the changes were picked up.
      return { passengers: compact(passengers) };
    });
  }

  switchFixedWaypoints(fixedWaypoint) {
    this.setState((prevState) => ({
      fixedWaypoint,
      origin: prevState.destination,
      destination: prevState.origin
    }));

    const otherWaypointMatchingMode = this.state.nearbyMatchingMode[fixedWaypoint];
    const otherWaypoint = fixedWaypoint === 'origin' ? 'destination' : 'origin'
    this.setNearbyMatchingMode(fixedWaypoint, false);
    this.setNearbyMatchingMode(otherWaypoint, otherWaypointMatchingMode);
  }

  initialRequestedBookingTime(props) {
    const notAdhocBookable = !props.product.ad_hoc_bookable && props.product.prebookable;
    const prebookableAsDefault =
      props.product.prebookable && !!props.product.booking_wizard_settings.prebooking_preselected;

    if (notAdhocBookable || prebookableAsDefault) {
      const baseTime = toZonedTime(
        roundToNearestMinutes(
          addSeconds(new Date(), props.product.prebooking_threshold.min),
          {
            nearestTo: 5
          }
        ),
        props.product.timezone.identifier
      );

      return baseTime;
    }

    return null;
  }

  initialState(props) {
    return {
      origin: null,
      destination: null,
      requestedTimeForWaypoint: 'origin',
      passengers: [newUninitializedPassenger(this.props.product)],
      options: initialRideOptionValues(this.props.product),
      fixedWaypoint: null,
      requestedTime: this.initialRequestedBookingTime(props),
      userId: null,
      ride: null,
      errors: null,
      warnings: null,
      spinnerMessage: null,
      currentTime: new Date(),
      paymentMethods: initialPaymentMethods(props.product),
      chosenPaymentMethod: null,
      previouslyRequestedPoints: [],
      showPrebookingModal: false,
      showStations: window.localStorage.showStations !== 'false',
      nearbyMatchingMode: {
        origin: true,
        destination: true
      },
      servedAreas: [],
      unservedAreas: [],
      passengerNoteToDriver: ''
    };
  }

  resetWizard() {
    this.setState(this.initialState(this.props));
  }

  bookAgain() {
    this.setState({
      ride: null,
      errors: null,
      warnings: null,
      spinnerMessage: null
    });

    if (this.productSupportsPrebookingAssistance() && this.state.requestedTime) {
      this.togglePrebookingModal();
    }
  }

  bookReturn() {
    const { destination: newOrigin, origin: newDestination } = this.state;
    this.setState({
      ride: null,
      origin: newOrigin,
      destination: newDestination,
      fixedWaypoint: this.fixedOriginOrDestination(newOrigin),
      errors: null,
      warnings: null
    });

    if (this.productSupportsPrebookingAssistance() && this.state.requestedTime) {
      this.togglePrebookingModal();
    }
  }

  togglePrebookingModal() {
    this.setState(prevState => ({ showPrebookingModal: !prevState.showPrebookingModal }));
  }

  toggleStationVisibility() {
    this.setState((prevState) => {
      const newValue = !prevState.showStations;
      window.localStorage.setItem('showStations', newValue.toString());
      return { showStations: newValue };
    });
  }

  // SPECIAL CLONING RIDE BEHAVIOR ---------------------------------------------

  initiateStateFromClonedRide() {
    const { cloningRide, cloningDirection } = this.props;
    let origin = null;
    let destination = null;

    if (cloningDirection === 'return') {
      origin = cleanExistingWaypoint(cloningRide.destination);
      destination = cleanExistingWaypoint(cloningRide.origin);
    } else {
      origin = cleanExistingWaypoint(cloningRide.origin);
      destination = cleanExistingWaypoint(cloningRide.destination);
    }

    const fixedWaypoint = this.fixedOriginOrDestination(origin);

    this.setNearbyMatchingMode(fixedWaypoint, false);

    this.setState({
      userId: cloningRide.user.id,
      origin,
      destination,
      fixedWaypoint,
      chosenPaymentMethod: cloningRide.payment_method || null,
      passengers: cloningRide.passengers.map((passenger) => {
        const { options, ...passengerWithoutOptions } = passenger;
        const optionsWithSlug = options.map((passengerOption) => (
          {
            slug: passengerOption.slug,
            value: passengerOption.value,
            type: passengerOption.type
          }
        ));

        return {
          ...passengerWithoutOptions,
          options: optionsWithSlug
        }
      })
    });
    // only set values that are on the cloned ride and leave default values that are not on the cloned ride
    const clonedOptions = Object.entries(cloningRide.options);

    clonedOptions.forEach(option => {
      this.setOption(option[0], option[1]);
    });
  }

  fixedOriginOrDestination(origin) {
    if (!this.productRequiresFixedStation()) {
      return null;
    }

    const fixedStations = this.props.product.fixed_stations;
    const originStationId = origin.station_id || origin.id;

    if (fixedStations.some((station) => station.id === originStationId)) {
      return 'origin';
    }

    return 'destination';
  }

  // TIMER CONTROLS ------------------------------------------------------------

  startTicker() {
    this.secondRefreshTimer = setInterval(() => {
      this.setState({
        currentTime: new Date()
      });
    }, 1000);
  }

  endTicker() {
    if (this.secondRefreshTimer) window.clearInterval(this.secondRefreshTimer);
  }

  // LOADING INDICATOR CONTROLS ------------------------------------------------

  // PREDICATES ----------------------------------------------------------------

  rideNeedsPaymentMethod() {
    return rideNeedsPaymentMethod(this.state);
  }

  rideIsCreateable() {
    return rideIsCreateable({ state: this.state, product: this.props.product });
  }

  rideIsBookable() {
    return rideIsBookable(this.state);
  }

  productSupportsPassengerNoteToDriver() {
    return productSupportsPassengerNoteToDriver(this.props.product);
  }

  productRequiresFixedStation() {
    return productRequiresFixedStation(this.props.product);
  }

  productSupportsPrebookingAssistance() {
    return productSupportsPrebookingAssistance(this.props.product);
  }

  // RENDER METHODS ------------------------------------------------------------

  destinationMarker() {
    if (!this.state.destination || this.state.ride) return null;
    return (
      <SVGMarker
        icon={dropoffIcon}
        color={colors.red}
        position={latLngFromObj(this.state.destination)}
        markerType="destination"
        size={[31, 45]}
        anchor={[15, 45]}
      />
    );
  }

  originMarker() {
    if (!this.state.origin || this.state.ride) return null;

    return (
      <SVGMarker
        icon={pickupIcon}
        color={colors.green}
        position={latLngFromObj(this.state.origin)}
        markerType="origin"
        size={[31, 45]}
        anchor={[15, 45]}
      />
    );
  }

  timeLeftToBook() {
    if (!this.state.ride || (this.state.ride && this.state.ride.state !== 'ready') || !this.rideIsBookable()) {
      return null;
    }

    const secondsLeft = timeDiff(this.state.currentTime, this.state.ride.valid_for_passenger_until);

    if (secondsLeft < 0) return null;

    return (
      <span className="badge badge-warning">
        {`${window.locales.BookingWizard.spinners.remaining}: ${secondsToHuman(secondsLeft)}`}
      </span>
    );
  }

  nextOptionButtons() {
    if (!this.state.ride || (this.state.ride && this.state.ride.state !== 'driver_accepted')) {
      return null;
    }

    return (
      <>
        <Button color="primary" size="lg" onClick={this.resetWizard}>
          {window.locales.BookingWizard.buttons.newRide}
        </Button>
        <Button color="success" size="lg" className="pl-3" onClick={this.bookAgain}>
          {window.locales.BookingWizard.buttons.bookAgain}
        </Button>
        <Button color="success" size="lg" onClick={this.bookReturn}>
          {window.locales.BookingWizard.buttons.bookReturn}
        </Button>
      </>
    );
  }

  createRideButton() {
    return (
      <button className="btn btn-primary btn-lg" type="button" onClick={this.createRide} disabled={!this.rideIsCreateable()}>
        {window.locales.BookingWizard.buttons.create}
      </button>
    );
  }

  bookRideButton() {
    if (!this.rideIsBookable()) return null;

    return (
      <button className="btn btn-success btn-lg" type="button" onClick={this.bookRide}>
        {window.locales.BookingWizard.buttons.book}
      </button>
    );
  }

  cancelRideButton() {
    if (!this.state.ride) return null;

    return (
      <button className="btn btn-danger btn-lg" type="button" onClick={this.cancelRide}>
        {window.locales.BookingWizard.buttons.cancel}
      </button>
    );
  }

  renderStationVisibilityToggle() {
    if (!this.props.stations || this.props.stations.length === 0) {
      return null;
    }

    return (
      <Control position="bottomleft" className="leaflet-control-layers">
        <input
            id="showStations"
            onChange={this.toggleStationVisibility}
            checked={this.state.showStations}
            type="checkbox"
        />
        &nbsp;
        <label htmlFor="showStations" className="col-form-label">
          {window.locales.HereMap.toggleStationVisibility}
        </label>
      </Control>
    );
  }

  renderStations() {
    if (!this.state.showStations) return null;

    return this.props.stations.map((station) => {
      const key = station.id;
      return <Station key={key} station={station} />;
    });
  }

  renderRide() {
    if (!this.state.ride || this.state.ride.state === 'searching') return null;

    return <Ride key={this.state.ride.id} ride={this.state.ride} color={colors.black} />;
  }

  renderErrors() {
    if (!this.state.errors || this.state.errors.length === 0) return null;

    return (
      <Alert type="danger">
        <ul>
          {this.state.errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  renderWarnings() {
    if (!this.state.warnings || this.state.warnings.length === 0) return null;

    return (
      <Alert type="warning">
        <ul>
          {this.state.warnings.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  renderPaymentMethodDropdown() {
    if (!this.rideNeedsPaymentMethod()) return null;

    return (
      <PaymentMethodDropdown
        label={window.locales.BookingWizard.paymentMethod}
        paymentMethods={this.state.paymentMethods}
        isDisabled={isEmpty(this.state.userId)}
        handleUpdate={this.setChosenPaymentMethod}
        chosenPaymentMethod={this.state.chosenPaymentMethod}
      />
    );
  }

  renderBookedMessage() {
    if (!this.state.ride || this.state.ride.state !== 'driver_accepted' || !this.state.ride.pickup) {
      return null;
    }

    return (
      <Alert type="success" title={window.locales.BookingWizard.message.title}>
        {window.locales.BookingWizard.message.success}
        <dl className="row">
          <dt className="col-sm-3 text-right">{window.locales.BookingWizard.RideDetails.pickup_time}</dt>
          <dd className="col-sm-9">
            {formattedTime(this.state.ride.pickup.time, this.props.product.timezone.identifier)}
          </dd>

          <dt className="col-sm-3 text-right">{window.locales.BookingWizard.RideDetails.ride_duration}</dt>
          <dd className="col-sm-9">
            {secondsToHuman(rideDuration(this.state.ride))}
            {window.locales.BookingWizard.RideDetails.minutes}
          </dd>

          <dt className="col-sm-3 text-right">{window.locales.BookingWizard.RideDetails.dropoff_time}</dt>
          <dd className="col-sm-9">
            {formattedTime(this.state.ride.dropoff.time, this.props.product.timezone.identifier)}
          </dd>
        </dl>
        <h3>
          {window.locales.BookingWizard.message.booking_code}
          :
          {' '}
          <strong>{this.state.ride.booking.verification_code}</strong>
        </h3>
      </Alert>
    );
  }

  renderAreas() {
    const areas = sortBy(
      compact([
        this.props.product.drt_area,
        this.props.product.intermodal_area,
        ...this.state.servedAreas,
        ...this.state.unservedAreas
      ]), "z_index")

    return areas.map((area) => <Area key={`area-${area.id}`}
                                     area={area.area}
                                     color={area.color}
                                     opacity={area.opacity}
                                     weight={area.stroke_weight}
                                     fillColor={area.fill_color}
                                     fillOpacity={area.fill_opacity} />);
  }

  renderSpinner() {
    if (!this.state.spinnerMessage) return null;

    return (
      <span className="badge badge-secondary">
        <i className="fa fa-spinner fa-pulse fa-fw" />
        <span>{this.state.spinnerMessage}</span>
      </span>
    );
  }

  renderRideInfos() {
    if (
      !this.state.ride ||
      !this.state.ride.pickup ||
      this.state.ride.state === 'searching' ||
      this.state.ride.state === 'cancelled'
    )
      return null;

    return (
      <RideDetails
        ride={this.state.ride}
        timezone={this.props.product.timezone.identifier}
      />
    );
  }

  renderTimePicker() {
    const { timezone, prebookable, prebooking_threshold: prebookingThreshold  } = this.props.product;
    if (!prebookable) {
      return null;
    }

    return (
      <PrebookingTime
        prebookingThresholdMax={prebookingThreshold.max}
        prebookingThresholdMin={prebookingThreshold.min}
        prebooking={!!this.state.requestedTime}
        handleUpdate={this.setRequestedTime}
        timezone={timezone.identifier}
        allowDestinationTime={!!this.props.product.destination_time_based_matching}
        requestedTimeForWaypoint={this.state.requestedTimeForWaypoint}
        requestedTime={this.state.requestedTime}
      />
    );
  }

  renderFixedWaypointSwitcher() {
    if (!this.productRequiresFixedStation()) {
      return null;
    }

    return (
      <ul className="list-group pb-2">
        <li className="list-group-item">
          {window.locales.BookingWizard.fixedStation}
          <span className="float-right">
            <WaypointSwitcher handleUpdate={this.switchFixedWaypoints} waypoint={this.state.fixedWaypoint} />
          </span>
        </li>
      </ul>
    );
  }

  renderPassengerNoteToDriver() {
    if (!this.productSupportsPassengerNoteToDriver()) {
      return null;
     }

    return (
      <PassengerNoteToDriver
        note={this.state.passengerNoteToDriver}
        handleUpdate={this.setPassengerNoteToDriver}
      />
    );
  }

  renderPrebookingShortcutsModal() {
    if (!this.props.product.prebookable) {
      return null;
    }

    return (
      <PreBookingShortcutsModal
        requestedTime={this.state.requestedTime}
        requestedTimeForWaypoint={this.state.requestedTimeForWaypoint}
        prebookingThresholdMin={this.props.product.prebooking_threshold.min}
        prebookingThresholdMax={this.props.product.prebooking_threshold.max}
        timezone={this.props.product.timezone.identifier}
        show={this.state.showPrebookingModal}
        toggle={this.togglePrebookingModal}
        setRequestedTime={this.setRequestedTime}
      />
    );
  }

  render() {
    return (
      <div className="booking-wizard">
        <h1>
          {window.locales.BookingWizard.title}
          <span className="float-right">
            {this.renderSpinner()}
            {this.timeLeftToBook()}
          </span>
        </h1>

        <div className="row">
          <div className="col-md-6">
            <UserSelect
              stellwerkAPI={this.api}
              selectedUserId={this.state.userId}
              existingUser={this.props.cloningRide.user}
              handleUpdate={this.setUser}
              provider={this.props.product.provider}
            />
            {this.renderTimePicker()}
            {this.renderFixedWaypointSwitcher()}
            <div className="row">
              <div className="col-md-6">
                <Card title={window.locales.BookingWizard.origin}>
                  <WaypointSelector
                    handleUpdate={this.setOrigin}
                    waypointType="origin"
                    waypoint={this.state.origin}
                    stations={this.props.stations}
                    productRequiresFixedStation={this.productRequiresFixedStation()}
                    fixedStations={this.props.product.fixed_stations}
                    fixedWaypoint={this.state.fixedWaypoint}
                    boundingBox={this.props.product.default_map_bounding_box}
                    previouslyRequestedPoints={this.state.previouslyRequestedPoints}
                    nearbyMatchingMode={this.state.nearbyMatchingMode.origin}
                    handleNearbyMatchingModeUpdate={this.setNearbyMatchingMode}
                  />
                </Card>
              </div>
              <div className="col-md-6">
                <Card title={window.locales.BookingWizard.destination} className="col-md-6">
                  <WaypointSelector
                    handleUpdate={this.setDestination}
                    waypointType="destination"
                    waypoint={this.state.destination}
                    stations={this.props.stations}
                    productRequiresFixedStation={this.productRequiresFixedStation()}
                    fixedStations={this.props.product.fixed_stations}
                    fixedWaypoint={this.state.fixedWaypoint}
                    boundingBox={this.props.product.default_map_bounding_box}
                    previouslyRequestedPoints={this.state.previouslyRequestedPoints}
                    nearbyMatchingMode={this.state.nearbyMatchingMode.destination}
                    handleNearbyMatchingModeUpdate={this.setNearbyMatchingMode}
                  />
                </Card>
              </div>
            </div>

            <RideOptions
              handleOptionUpdate={this.setOption}
              handlePassengerUpdate={this.setPassengers}
              handleAddPassenger={this.addPassenger}
              dynamicRideOptions={this.props.product.product_ride_options}
              options={this.state.options}
              passengers={this.state.passengers}
              passengerOptions={this.props.product.passenger_options}
              passengerTypes={this.props.product.passenger_types}
              nameRequired={this.props.product.requires_passenger_names_or_ticket}

            />
            {this.renderPassengerNoteToDriver()}
          </div>
          <div className="col-md-6">
            {this.renderBookedMessage()}
            {this.renderErrors()}
            {this.renderWarnings()}
            {this.renderPaymentMethodDropdown()}
            {this.renderRideInfos()}
            <div className="actions clearfix">
              <div className="float-left">{this.nextOptionButtons()}</div>
              <div className="float-right">
                {this.createRideButton()}
                {this.bookRideButton()}
                {this.cancelRideButton()}
              </div>
            </div>
            <div className="map-container fullwidth-map pt-3">
              <HereMap boundingBox={this.props.product.default_map_bounding_box}>
                {this.renderAreas()}
                {this.renderRide()}
                {this.renderStationVisibilityToggle()}
                {this.renderStations()}
                {this.destinationMarker()}
                {this.originMarker()}
              </HereMap>
            </div>
          </div>
        </div>
        {this.renderPrebookingShortcutsModal()}
      </div>
    );
  }
}

BookingWizard.defaultProps = {
  cloningRide: {}
};

BookingWizard.propTypes = {
  product: PropTypes.object.isRequired,
  stations: PropTypes.array.isRequired,
  areas: PropTypes.array.isRequired,
  cloningRide: PropTypes.object,
  cloningDirection: PropTypes.oneOf(['same', 'return'])
};
